import React, { useContext } from 'react';
import Accordion from '../../common/Accordion';
import SliderInput from '../../common/SliderInput';
import Select from '../../common/Select';
import MultilineTooltip from '../../common/MultilineTooltip';
import Flex, { FlexDirection, FlexJustification } from '../../common/Flex';
import { HomeContext } from '../Home';
import { useState } from 'react';
import { useAppDispatch, useAppSelector, useTranslate } from '../../../hooks/common';
import { useHook } from '../../../hooks';
import { LoadSliderInputType, LoadType, StartingType } from '../../../enums';
import { getClosestRangeRound, getDefaultLoadData, getTorqueUnit, isDisabledByRange, isNullOrUndefined, mapToSliderInputText } from '../../../services';
import { LoadSliderInputValues, LoadSliderInputVisibilities, MesRequest, Range, SliderInputValue } from '../../../models';
import { DEFAULT_LOAD_TAGS, DEFAULT_LOAD_RANGE, DOUBLE_REGEX, TRANSLATIONS } from '../../../constants';
import { setLoadSliderTexts, setTags } from '../../../store';

interface LoadDataProps {
    disabled: boolean;
}

const LoadData = ({ disabled }: LoadDataProps) => {
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const isStartingTypeDol = mesRequest.commonData.startingType === StartingType.DOL;
    const [speedAccordionOpen, toggleSpeedAccordion] = useState(true);
    const [torqueAccordionOpen, toggleTorqueAccordion] = useState(true);
    const loadSliderTexts = useAppSelector(state => state.load.loadSliderTexts);
    const loadSliderRanges = useAppSelector(state => state.load.loadSliderRanges);
    const { getMainBoundary } = useContext(HomeContext);
    const { onLoadChange, calculateLoadTextAndRange, getChangeHandlerForProperty, setCustomLoadPointsTorqueDefaultValues } = useHook(x => x.loadData)();

    const isSliderDisabled = (range: Range | null) =>
        disabled || range === null ? true : isDisabledByRange(range);

    const getSliderInputValue = (range: Range | null, sliderInputValue: SliderInputValue | null): SliderInputValue =>
        range && sliderInputValue ? sliderInputValue : { sliderValue: DEFAULT_LOAD_RANGE.min, inputValue: '' };

    const getLoadSliderInputVisibilities = (): LoadSliderInputVisibilities => {
        const { n0, n1, n2, n3, n4, m0, m1, m2, m3, m4 } = loadSliderRanges;

        return {
            n0M0Visibility: Boolean(n0 || m0),
            n1M1Visibility: Boolean(n1 || m1),
            n2M2Visibility: Boolean(n2 || m2),
            n3M3Visibility: Boolean(n3 || m3),
            n4M4Visibility: Boolean(n4 || m4)
        };
    };

    const handleSliderChange = (value: number, property: string) => {
        dispatch(setLoadSliderTexts({ [property]: mapToSliderInputText(value) }));
    };

    const handleSliderBlur = (value: number, property: string) => {
        dispatch(setTags({ [property]: value }));

        getChangeHandlerForProperty(property as keyof LoadSliderInputValues, value);
        const newMesRequest: MesRequest = { ...mesRequest, loadData: { ...mesRequest.loadData, [property]: value } };

        if (mesRequest.loadData.loadType === LoadType.Custom) {
            setCustomLoadPointsTorqueDefaultValues(property as keyof LoadSliderInputValues, newMesRequest);
        }
    };

    const handleSliderInputBlur = (value: number, property: string) => {
        const key = property as keyof LoadSliderInputValues;
        const { min, max, step } = loadSliderRanges[key] as Range;
        const lessThanMinOrNull = value < min || isNullOrUndefined(value);
        let newSliderValue: SliderInputValue;

        if (lessThanMinOrNull || value > max) {
            const correctValue = lessThanMinOrNull ? min : max;
            newSliderValue = mapToSliderInputText(correctValue);

            dispatch(setLoadSliderTexts({ ...loadSliderTexts, [key]: newSliderValue }));
        } else {
            newSliderValue = {
                ...loadSliderTexts[key],
                sliderValue: getClosestRangeRound(value, min, step)
            } as SliderInputValue;

            dispatch(setLoadSliderTexts({ ...loadSliderTexts, [key]: newSliderValue }));
        }

        dispatch(setTags({ [property]: newSliderValue.inputValue }));
        
        getChangeHandlerForProperty(key, Number(newSliderValue.inputValue));
        const newMesRequest: MesRequest = { ...mesRequest, loadData: { ...mesRequest.loadData, [key]: Number(newSliderValue.inputValue) } };

        if (mesRequest.loadData.loadType === LoadType.Custom) {
            setCustomLoadPointsTorqueDefaultValues(key, newMesRequest);
        }
    };
    
    const handleSliderInputChange = (value: string, property: string) => {
        if (DOUBLE_REGEX.test(value)) {
            dispatch(setLoadSliderTexts({ ...loadSliderTexts, [property]: { ...loadSliderTexts[property as keyof LoadSliderInputValues], inputValue: value } }));
        }
    };

    const handleLoadTypeSelectChange = async (value: { [key in LoadType]?: string }) => {
        const newLoadType = Number(Object.keys(value));
        const defaultLoadData = getDefaultLoadData(newLoadType, mesRequest);

        calculateLoadTextAndRange(defaultLoadData);

        dispatch(setTags({ ...DEFAULT_LOAD_TAGS, loadType: newLoadType }));

        await onLoadChange(defaultLoadData);
    };

    const renderLoadTypeSelect = (type: string) => {
        const { loadType } = mesRequest.loadData;
        const loadTypes: { [key in LoadType]?: string }[] = [
            { '0': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.QuadraticWithoutBreakaway) },
            { '1': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.QuadraticWithBreakaway) },
            { '2': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.Constant) },
            { '3': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.Linear) },
            { '4': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.NoLoad) }
        ];

        if (isStartingTypeDol) {
            loadTypes.push({ '5': translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.Custom) });
        }

        //change idName to something else to avoid duplicate ids in the DOM
        return <div className='select-with-tooltip'>
            <Select
                disabled={disabled}
                label={translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.title)}
                value={loadTypes[loadType]}
                values={loadTypes}
                mapToString={x => Object.values(x).toString()}
                onSelect={x => handleLoadTypeSelectChange(x)}
            />
            <MultilineTooltip idName={type} text={translate(TRANSLATIONS.tooltips.loadCharacteristics)} html>
                <></>
            </MultilineTooltip>
        </div>;
    };

    const renderSpeedSliders = (loadSliderInputVisibilities: LoadSliderInputVisibilities) => {
        const { n0, n1, n2, n3, n4 } = loadSliderTexts;
        const { n0M0Visibility, n1M1Visibility, n2M2Visibility, n3M3Visibility, n4M4Visibility } = loadSliderInputVisibilities;

        return (
            <>
                {n0M0Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.n0]}
                        disabled={isSliderDisabled(loadSliderRanges.n0)}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n0)}
                        value={getSliderInputValue(loadSliderRanges.n0, n0)}
                        range={loadSliderRanges.n0 || DEFAULT_LOAD_RANGE}
                        unit={translate(TRANSLATIONS.units.rpm)}
                    />
                }
                {n1M1Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.n1]}
                        disabled={isSliderDisabled(loadSliderRanges.n1)}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n1)}
                        value={getSliderInputValue(loadSliderRanges.n1, n1)}
                        range={loadSliderRanges.n1 || DEFAULT_LOAD_RANGE}
                        unit={translate(TRANSLATIONS.units.rpm)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n2M2Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.n2]}
                        disabled={isSliderDisabled(loadSliderRanges.n2)}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n2)}
                        range={loadSliderRanges.n2 || DEFAULT_LOAD_RANGE}
                        unit={translate(TRANSLATIONS.units.rpm)}
                        value={getSliderInputValue(loadSliderRanges.n2, n2)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n3M3Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.n3]}
                        disabled={isSliderDisabled(loadSliderRanges.n3)}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n3)}
                        range={loadSliderRanges.n3 || DEFAULT_LOAD_RANGE}
                        value={getSliderInputValue(loadSliderRanges.n3, n3)}
                        unit={translate(TRANSLATIONS.units.rpm)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n4M4Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.n4]}
                        disabled={isSliderDisabled(loadSliderRanges.n4)}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n4)}
                        range={loadSliderRanges.n4 || DEFAULT_LOAD_RANGE}
                        unit={translate(TRANSLATIONS.units.rpm)}
                        value={getSliderInputValue(loadSliderRanges.n4, n4)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
            </>
        );
    };

    const renderTorqueSliders = (loadSliderInputVisibilities: LoadSliderInputVisibilities) => {
        const { m0, m1, m2, m3, m4 } = loadSliderTexts;
        const { n0M0Visibility, n1M1Visibility, n2M2Visibility, n3M3Visibility, n4M4Visibility } = loadSliderInputVisibilities;
        const unit = getTorqueUnit(mesRequest.unitSystem);

        return (
            <>
                {n0M0Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.m0]}
                        disabled={isSliderDisabled(loadSliderRanges.m0)}
                        label={`${translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m0)} ${unit}`}
                        range={loadSliderRanges.m0 || DEFAULT_LOAD_RANGE}
                        unit={unit}
                        value={getSliderInputValue(loadSliderRanges.m0, m0)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n1M1Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.m1]}
                        range={loadSliderRanges.m1 || DEFAULT_LOAD_RANGE}
                        label={`${translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m1)} ${unit}`}
                        disabled={isSliderDisabled(loadSliderRanges.m1)}
                        unit={unit}
                        value={getSliderInputValue(loadSliderRanges.m1, m1)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n2M2Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.m2]}
                        disabled={isSliderDisabled(loadSliderRanges.m2)}
                        label={`${translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m2)} ${unit}`}
                        range={loadSliderRanges.m2 || DEFAULT_LOAD_RANGE}
                        unit={unit}
                        value={getSliderInputValue(loadSliderRanges.m2, m2)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n3M3Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.m3]}
                        disabled={isSliderDisabled(loadSliderRanges.m3)}
                        label={`${translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m3)} ${unit}`}
                        range={loadSliderRanges.m3 || DEFAULT_LOAD_RANGE}
                        unit={unit}
                        value={getSliderInputValue(loadSliderRanges.m3, m3)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
                {n4M4Visibility &&
                    <SliderInput
                        name={LoadSliderInputType[LoadSliderInputType.m4]}
                        disabled={isSliderDisabled(loadSliderRanges.m4)}
                        label={`${translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m4)} ${unit}`}
                        range={loadSliderRanges.m4 || DEFAULT_LOAD_RANGE}
                        unit={unit}
                        value={getSliderInputValue(loadSliderRanges.m4, m4)}
                        onInputChange={(value, name) => handleSliderInputChange(value, name)}
                        onInputBlur={(value, name) => handleSliderInputBlur(value, name)}
                        onSliderChange={(value, name) => handleSliderChange(value, name)}
                        onSliderBlur={(value, name) => handleSliderBlur(value, name)}
                    />
                }
            </>
        );
    };
    
    return (
        <div className='load-container'>
            <Flex direction={FlexDirection.Row} justification={FlexJustification.Center}>
                <div>
                    <Accordion
                        key={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.title)}
                        iconFont={'e93f'}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.title)}
                        open={speedAccordionOpen}
                        onClick={() => toggleSpeedAccordion(!speedAccordionOpen)}
                    >
                        <Flex direction={FlexDirection.Column} justification={FlexJustification.Center} gap={15}>
                            {renderLoadTypeSelect('speed')}
                            {loadSliderRanges && renderSpeedSliders(getLoadSliderInputVisibilities())}
                        </Flex>
                    </Accordion>
                </div>
                <div>
                    <Accordion
                        key={translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.title)}
                        iconFont={'e93f'}
                        label={translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.title)}
                        open={torqueAccordionOpen}
                        onClick={() => toggleTorqueAccordion(!torqueAccordionOpen)}
                    >
                        <Flex direction={FlexDirection.Column} justification={FlexJustification.Center} gap={15}>
                            {renderLoadTypeSelect('torque')}
                            {loadSliderRanges && renderTorqueSliders(getLoadSliderInputVisibilities())}
                        </Flex>
                    </Accordion>
                </div>
            </Flex>
        </div>
    );
};

export default LoadData;
